import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Column } from "@tanstack/react-table"
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon, XCircle } from "lucide-react"

import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ActiveFilterIcon, use_searchable_choices } from "@/global_filter"
import { SearchableSelect } from "@/components/searchable_select"
import { classes } from "@/utils"
import { capitalCase } from "change-case"
import type { ComponentChildren } from "preact"
import { useState } from "preact/compat"
import { DEDUCTION_COLUMN_FILTER_PLACEHOLDER, SearchInput } from "./debounced_input"

/** One option of a [`ColumnChoiceFilter`]: the stored filter value and the words it shows as. */
export interface ColumnChoice {
  value: string
  label: string
}

const ALL_CHOICES = "__all__"

interface ColumnChoiceFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  choices: ColumnChoice[]
  /** The "no filter" option's label. */
  all_label?: string
  className?: string
}

/**
 * A single-select column filter that shows each choice as given (customer names, month labels),
 * where [`ColumnHeader`]'s `choices` prop rewrites them in title case. Pass it as `filterContent`,
 * or render it on its own in a toolbar — both drive the same column filter.
 */
export function ColumnChoiceFilter<TData, TValue>({ column, choices, all_label = "All", className }: ColumnChoiceFilterProps<TData, TValue>) {
  const value = column.getFilterValue()
  const current = typeof value === "string" && value !== "" ? value : ALL_CHOICES
  function set_choice(choice: string) {
    column.setFilterValue(choice === ALL_CHOICES ? undefined : choice)
  }
  // A long list is searched, the way the global customer filter searches its customers.
  if (use_searchable_choices(choices.length)) {
    return (
      <SearchableSelect
        options={[{ value: ALL_CHOICES, label: all_label }, ...choices]}
        value={current}
        onValueChange={set_choice}
        placeholder={all_label}
        searchPlaceholder="Search..."
        emptyMessage="No matches."
        triggerClassName={className}
      />
    )
  }
  return (
    <Select
      value={typeof value === "string" && value !== "" ? value : ALL_CHOICES}
      onValueChange={(choice: string) => column.setFilterValue(choice === ALL_CHOICES ? undefined : choice)}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={all_label} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={ALL_CHOICES}>{all_label}</SelectItem>
          {choices.map((choice: ColumnChoice) => (
            <SelectItem key={choice.value} value={choice.value}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
  choices?: string[]
  onSelectChoice?: (choice: string) => void
  /** When set, replaces the default search / single-select filter UI (e.g. StageMultiSelect). */
  filterContent?: ComponentChildren
}

export function ColumnHeader<TData, TValue>({
  column,
  title,
  className,
  choices,
  onSelectChoice,
  filterContent,
}: DataTableColumnHeaderProps<TData, TValue>) {
  const [open, setOpen] = useState(false)

  if (!column.getCanSort()) {
    return <div className={classes(className)}>{title}</div>
  }

  function handleFilterChange(value: string | number) {
    column.setFilterValue(String(value))
  }

  function getColumnIcon() {
    if (column.getIsFiltered()) return <ActiveFilterIcon />
    const sort = column.getIsSorted()
    return sort === "asc" ? (
      <ArrowUpIcon className="h-4 w-4" />
    ) : sort === "desc" ? (
      <ArrowDownIcon className="h-4 w-4" />
    ) : (
      <ChevronsUpDownIcon className="ml-2 h-4 w-4" />
    )
  }

  function renderFilter() {
    if (filterContent !== undefined) {
      return filterContent
    }

    if (choices) {
      return (
        <Select onValueChange={onSelectChoice} defaultValue={(column.getFilterValue() as string) ?? "all"}>
          <SelectTrigger>
            <SelectValue placeholder="" />
            <ArrowDownIcon className="h-4 w-4" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem key={0} value="all">
                &nbsp;
              </SelectItem>
              {choices.map((choice, idx) => (
                <SelectItem key={idx + 1} value={choice}>
                  {capitalCase(choice)}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )
    }

    return (
      <SearchInput
        value={(column.getFilterValue() as string) ?? ""}
        onChange={handleFilterChange}
        className="min-w-96"
        placeholder={DEDUCTION_COLUMN_FILTER_PLACEHOLDER}
        autoFocus
        focusTrigger={open}
      />
    )
  }

  // Prevent the popover from closing when interacting with Select portals
  function handleInteractOutside(event: Event) {
    const target = event.target as HTMLElement
    if (target?.closest?.("[data-radix-select-content]") || target?.closest?.("[data-radix-popper-content-wrapper]")) {
      event.preventDefault()
    }
  }

  const menuItemClass =
    "flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-hidden hover:bg-accent hover:text-accent-foreground"

  return (
    <div className={classes("flex items-center", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div class="inline-flex items-center h-6 w-full justify-between cursor-pointer text-sm whitespace-nowrap">
            <span>{title}</span>
            {getColumnIcon()}
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-1" onInteractOutside={handleInteractOutside}>
          <button class={menuItemClass} onClick={() => column.toggleSorting(false)}>
            <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Asc
          </button>
          <button class={menuItemClass} onClick={() => column.toggleSorting(true)}>
            <ArrowDownIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Desc
          </button>
          <div class="-mx-1 my-1 h-px bg-muted" />
          <button class={menuItemClass} onClick={() => column.setFilterValue(undefined)}>
            <XCircle className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
            Clear Filter
          </button>
          <div class="px-2 py-1.5">{renderFilter()}</div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
